import { Component, OnInit } from '@angular/core';


interface AdminActivity {
  timestamp: Date;
  user: string;
  action: string;
  status: 'Success' | 'Failed';
}

interface QualityMetric {
  id: string;
  title: string;
  score: number;
  maxScore: number;
  trend: 'up' | 'down' | 'stable';
  department: string;
}

interface CourseEvaluation {
  courseCode: string;
  courseName: string;
  instructor: string;
  rating: number;
  responseRate: number;
  evaluationDate: Date;
  status: 'Completed' | 'Pending' | 'In Progress';
}

interface AccreditationItem {
  standard: string;
  complianceLevel: number;
  lastReview: Date;
  nextReview: Date;
  status: 'Compliant' | 'Needs Improvement' | 'Non-Compliant';
}

@Component({
  selector: 'app-qadashboard',
  templateUrl: './qadashboard.component.html',
  styleUrls: ['./qadashboard.component.scss']
})
export class QadashboardComponent implements OnInit {

  recentActivities: AdminActivity[] = [];
  currentTime: Date = new Date();
  
  // Quality Metrics Overview
  overallQualityScore: number = 0;
  totalEvaluations: number = 0;
  pendingReviews: number = 0;
  complianceRate: number = 0;

  // Quality Metrics
  qualityMetrics: QualityMetric[] = [];

  // Course Evaluations
  recentEvaluations: CourseEvaluation[] = [];

  // Accreditation Status
  accreditationItems: AccreditationItem[] = [];

  // Charts data (you can integrate with actual chart libraries)
  trendData: any[] = [];

  constructor() { }

  ngOnInit(): void {
    this.initializeMetrics();
    this.loadRecentEvaluations();
    this.loadAccreditationStatus();
  }

  private initializeMetrics(): void {
    // Overview Metrics
    this.overallQualityScore = 87;
    this.totalEvaluations = 156;
    this.pendingReviews = 12;
    this.complianceRate = 94;

    // Quality Metrics by Category
    this.qualityMetrics = [
      {
        id: '1',
        title: 'Teaching Quality',
        score: 4.3,
        maxScore: 5.0,
        trend: 'up',
        department: 'Academic'
      },
      {
        id: '2',
        title: 'Course Content',
        score: 4.1,
        maxScore: 5.0,
        trend: 'stable',
        department: 'Curriculum'
      },
      {
        id: '3',
        title: 'Student Satisfaction',
        score: 4.5,
        maxScore: 5.0,
        trend: 'up',
        department: 'Student Affairs'
      },
      {
        id: '4',
        title: 'Infrastructure',
        score: 3.8,
        maxScore: 5.0,
        trend: 'down',
        department: 'Facilities'
      },
      {
        id: '5',
        title: 'Learning Outcomes',
        score: 4.2,
        maxScore: 5.0,
        trend: 'up',
        department: 'Academic'
      },
      {
        id: '6',
        title: 'Research Quality',
        score: 4.0,
        maxScore: 5.0,
        trend: 'stable',
        department: 'Research'
      }
    ];
  }

  private loadRecentEvaluations(): void {
    this.recentEvaluations = [
      {
        courseCode: 'CS301',
        courseName: 'Database Systems',
        instructor: 'Dr. Mensah',
        rating: 4.5,
        responseRate: 87,
        evaluationDate: new Date('2025-09-15'),
        status: 'Completed'
      },
      {
        courseCode: 'BUS202',
        courseName: 'Business Analytics',
        instructor: 'Prof. Owusu',
        rating: 4.2,
        responseRate: 92,
        evaluationDate: new Date('2025-09-20'),
        status: 'Completed'
      },
      {
        courseCode: 'ENG105',
        courseName: 'Technical Writing',
        instructor: 'Dr. Agyemang',
        rating: 4.7,
        responseRate: 78,
        evaluationDate: new Date('2025-09-25'),
        status: 'In Progress'
      },
      {
        courseCode: 'MATH201',
        courseName: 'Advanced Calculus',
        instructor: 'Dr. Boateng',
        rating: 3.9,
        responseRate: 65,
        evaluationDate: new Date('2025-09-28'),
        status: 'Pending'
      },
      {
        courseCode: 'PHY303',
        courseName: 'Quantum Mechanics',
        instructor: 'Prof. Asante',
        rating: 4.4,
        responseRate: 88,
        evaluationDate: new Date('2025-09-10'),
        status: 'Completed'
      }
    ];
  }

  private loadAccreditationStatus(): void {
    this.accreditationItems = [
      {
        standard: 'Academic Standards & Quality',
        complianceLevel: 95,
        lastReview: new Date('2024-06-15'),
        nextReview: new Date('2026-06-15'),
        status: 'Compliant'
      },
      {
        standard: 'Faculty Qualifications',
        complianceLevel: 92,
        lastReview: new Date('2024-08-20'),
        nextReview: new Date('2026-08-20'),
        status: 'Compliant'
      },
      {
        standard: 'Learning Resources',
        complianceLevel: 78,
        lastReview: new Date('2024-09-10'),
        nextReview: new Date('2026-09-10'),
        status: 'Needs Improvement'
      },
      {
        standard: 'Student Support Services',
        complianceLevel: 88,
        lastReview: new Date('2024-07-05'),
        nextReview: new Date('2026-07-05'),
        status: 'Compliant'
      },
      {
        standard: 'Assessment & Evaluation',
        complianceLevel: 96,
        lastReview: new Date('2024-05-12'),
        nextReview: new Date('2026-05-12'),
        status: 'Compliant'
      }
    ];
  }

  getScorePercentage(score: number, maxScore: number): number {
    return (score / maxScore) * 100;
  }

  getTrendIcon(trend: string): string {
    switch(trend) {
      case 'up': return '↑';
      case 'down': return '↓';
      default: return '→';
    }
  }

  getStatusClass(status: string): string {
    switch(status) {
      case 'Completed':
      case 'Compliant':
        return 'status-success';
      case 'Pending':
      case 'Needs Improvement':
        return 'status-warning';
      case 'In Progress':
        return 'status-info';
      case 'Non-Compliant':
        return 'status-danger';
      default:
        return '';
    }
  }
}